/**
 * Anomalies Page
 * Displays cost anomalies with filtering and acknowledgment
 */

import React, { useState, useEffect } from 'react';
import { withFreshData } from '../services/apiHeaders';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { RefreshCw, Loader2, AlertTriangle, AlertOctagon, DollarSign, Activity, Eye, Link2 } from 'lucide-react';
import {
  getAnomalies,
  getAnomalySummary,
  Anomaly,
  AnomalySummary,
} from '../services/anomalyService';
import { CloudProviderIcon } from '../components/common/CloudProviderIcon';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { Button } from '../components/common/Button';
import { IconButton } from '../components/common/IconButton';
import { Badge, BadgeVariant } from '../components/common/Badge';
import { StatCard } from '../components/common/StatCard';
import { ProviderTiles } from '../components/common/ProviderTiles';
import {
  confidenceLabel,
  formatCurrency,
  formatRelativeDate as formatDate,
  formatType,
  getSeverityVariant,
  getStatusVariant,
  getTypeVariant,
} from '../utils/anomalyDisplay';

const AnomaliesPage: React.FC = () => {
  const anomalyDetectionEnabled = useFeatureFlag('anomaly_detection_enabled');
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [summary, setSummary] = useState<AnomalySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Filters
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  // The backend hides informational findings (new_service, sub-100% increases)
  // by default. That filter used to be invisible, so a tenant with real open
  // findings saw "No Anomalies Found - great news!". These two pieces of state
  // make it visible and escapable.
  const [showAll, setShowAll] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);

  // Load anomalies
  const loadAnomalies = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        showAll: showAll || undefined,
      };

      if (providerFilter !== 'all') filters.provider = providerFilter;
      if (severityFilter !== 'all') filters.severity = severityFilter;
      if (typeFilter !== 'all') filters.type = typeFilter;

      const [anomalyData, summaryData] = await Promise.all([
        getAnomalies(filters),
        getAnomalySummary(providerFilter !== 'all' ? providerFilter as any : undefined),
      ]);

      setAnomalies(anomalyData.anomalies);
      setHiddenCount(anomalyData.hidden_by_default_filter || 0);
      setSummary(summaryData);
    } catch (err: any) {
      console.error('Failed to load anomalies:', err);
      setError(err.message || 'Failed to load anomalies');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadAnomalies();
  }, [providerFilter, severityFilter, typeFilter, statusFilter, showAll]);

  // Legacy deep links of the form /anomalies?id=<uuid> used to pop the detail
  // modal open over this list. The detail view is its own route now, so those
  // links forward to it instead of quietly doing nothing. `replace` keeps the
  // dead ?id= URL out of the back-button history.
  useEffect(() => {
    const anomalyId = searchParams.get('id');
    if (anomalyId) navigate(`/anomalies/${anomalyId}`, { replace: true });
  }, [searchParams, navigate]);

  // Refresh anomalies
  const handleRefresh = () => {
    setRefreshing(true);
    // withFreshData adds Cache-Control: no-cache for everything loaded inside,
    // so this is a real refetch rather than a re-render of cached data.
    withFreshData(() => loadAnomalies());
  };

  // Acknowledge / resolve / false-positive handlers deliberately removed from
  // this page: those actions now live only on the anomaly detail page, which owns
  // the same calls plus proper in-modal confirmation and toasts (this page's
  // versions used raw window.prompt/alert). Keeping duplicates here would
  // mean two divergent implementations of the same three verdicts.

  if (anomalyDetectionEnabled === false) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center">
        <h2 className="text-sm font-semibold text-foreground">Anomaly Detection is turned off</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This feature is disabled for your account. Turn it on in Admin Console → Feature Flags.
        </p>
      </div>
    );
  }

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary/30 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Detecting anomalies...</p>
          <p className="text-sm text-muted-foreground mt-2">
            This may take a moment as we analyze cost patterns
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-lg p-6">
        <h2 className="text-sm font-semibold text-rose-600 dark:text-rose-400 mb-2">Error Loading Anomalies</h2>
        <p className="text-rose-600 text-sm">{error}</p>
        <Button onClick={handleRefresh} variant="danger" size="sm" className="mt-3">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-end gap-2">
        <Button onClick={handleRefresh} disabled={refreshing} size="sm">
          {refreshing ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Provider selector FIRST, then the summary cards - the same order
          Recommendations, Cost Explorer and the Dashboard use. The tiles
          scope everything below them, including the numbers on those cards,
          so they have to come first; this page had them the other way round,
          which read as if the cards were page totals independent of the
          provider filter. */}
      <ProviderTiles value={providerFilter} onChange={setProviderFilter} />

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <StatCard label="Active Anomalies" value={summary.active_anomalies} icon={Activity} accent="primary" />
          <StatCard label="Critical" value={summary.by_severity.critical} icon={AlertOctagon} accent="danger" />
          <StatCard label="High" value={summary.by_severity.high} icon={AlertTriangle} accent="warning" />
          <StatCard label="Total Impact" value={formatCurrency(summary.total_impact)} icon={DollarSign} accent="primary" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-lg border border-border shadow-sm p-3 dark:border-border dark:bg-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">
              Severity
            </label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="w-full h-9 px-3 py-0 text-sm leading-none border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-ring dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">
              Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full h-9 px-3 py-0 text-sm leading-none border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-ring dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="all">All Types</option>
              <option value="cost_spike">Cost Spike</option>
              {/* cost_drift is emitted by cost-drift.service.ts but was missing
                  from this list, so those anomalies existed and were unreachable
                  through the filter. */}
              <option value="cost_drift">Cost Drift</option>
              <option value="pattern_anomaly">Pattern Anomaly</option>
              <option value="new_service">New Service</option>
              <option value="unusual_usage">Unusual Usage</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/80 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full h-9 px-3 py-0 text-sm leading-none border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-ring dark:border-border dark:bg-card dark:text-foreground"
            >
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
              <option value="false_positive">False Positive</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>
      </div>

      {/* Anomalies List */}
      {/* The default view deliberately hides informational findings, but must
          say so - claiming "no anomalies, great news!" while real open
          findings exist is actively misleading. */}
      {hiddenCount > 0 && !showAll && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 dark:border-sky-900 dark:bg-sky-900/20">
          <span className="text-sm text-sky-800 dark:text-sky-200">
            {hiddenCount} informational {hiddenCount === 1 ? 'finding is' : 'findings are'} hidden — new-service appearances and
            increases under 100%, which are usually normal variance rather than real waste.
          </span>
          <button
            onClick={() => setShowAll(true)}
            className="shrink-0 rounded-md border border-sky-300 bg-card px-3 py-1.5 text-xs font-medium text-sky-800 hover:bg-sky-100 dark:border-sky-700 dark:bg-transparent dark:text-sky-200 dark:hover:bg-sky-900/40"
          >
            Show all findings
          </button>
        </div>
      )}
      {showAll && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3 dark:border-border dark:bg-card/40">
          <span className="text-sm text-foreground/80">
            Showing all findings, including informational ones.
          </span>
          <button
            onClick={() => setShowAll(false)}
            className="shrink-0 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground/80 hover:bg-secondary dark:border-border dark:bg-transparent dark:text-foreground dark:hover:bg-secondary"
          >
            Hide informational
          </button>
        </div>
      )}

      {anomalies.length === 0 ? (
        <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
          <svg
            className="w-16 h-16 text-muted-foreground/70 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {hiddenCount > 0 ? 'No cost anomalies in this view' : 'No Anomalies Found'}
          </h3>
          <p className="text-muted-foreground">
            {hiddenCount > 0
              ? `Nothing crossed the cost-anomaly threshold, but ${hiddenCount} informational ${hiddenCount === 1 ? 'finding is' : 'findings are'} available above.`
              : 'Your costs are following expected patterns.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {anomalies.map((anomaly) => (
            <div
              key={anomaly.id}
              className={`bg-card rounded-lg shadow-sm border-2 p-4 ${
                anomaly.severity === 'critical'
                  ? 'border-rose-300 dark:border-rose-800'
                  : anomaly.severity === 'high'
                  ? 'border-orange-300 dark:border-orange-800'
                  : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Badge variant={getSeverityVariant(anomaly.severity)}>{anomaly.severity.toUpperCase()}</Badge>
                    <Badge variant={getTypeVariant(anomaly.type)}>{formatType(anomaly.type)}</Badge>
                    <Badge variant={anomaly.provider as BadgeVariant} aria-label={`Provider: ${anomaly.provider.toUpperCase()}`}>
                      <CloudProviderIcon provider={anomaly.provider} size={12} className="mr-1" />
                      {anomaly.provider.toUpperCase()}
                    </Badge>
                    {/* FR-ANOM-010 - a quick visual cue that this row is one
                        of several related anomalies, before the detail page's
                        fuller cluster narrative. */}
                    {anomaly.cluster_id && (
                      <Badge variant="warning" title="Detected together with other anomalies in the same short window">
                        <Link2 className="mr-1 h-3 w-3" />
                        Clustered
                      </Badge>
                    )}
                    {(() => {
                      const conf = confidenceLabel(anomaly);
                      return conf ? (
                        <Badge variant="muted" title={conf.title}>
                          {conf.text}
                        </Badge>
                      ) : null;
                    })()}
                    {anomaly.status !== 'open' && (
                      <Badge variant={getStatusVariant(anomaly.status)}>
                        {anomaly.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{formatDate(anomaly.detected_at)}</span>
                  </div>

                  {/* Message */}
                  <p className="text-sm font-semibold text-foreground mb-2">
                    {anomaly.message}
                  </p>

                  {/* Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Service:</span>
                      <span className="ml-1 font-medium text-foreground">
                        {anomaly.service}
                      </span>
                    </div>
                    {anomaly.metadata?.primary_resource && (
                      <div>
                        <span className="text-muted-foreground">Resource:</span>
                        <span className="ml-1 font-medium text-foreground">
                          {anomaly.metadata.primary_resource}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Normal Cost:</span>
                      <span className="ml-1 font-medium text-foreground">
                        {formatCurrency(anomaly.normal_cost)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Current Cost:</span>
                      <span className="ml-1 font-bold text-rose-600 dark:text-rose-400">
                        {formatCurrency(anomaly.current_cost)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Acknowledge / Resolve / False-positive live in the detail
                    view only. Deciding an anomaly is real, fixed, or a false
                    positive is a judgement that needs the evidence on that
                    screen (baseline, cost trend, root cause) - offering those
                    verdicts on a summary card invites them to be clicked
                    without any of it, and crowded every row with four
                    same-weight buttons. */}
                {/* One canonical "open detail" affordance across the whole
                    app: a ghost Eye icon button. This row used to be a
                    SearchCheck icon plus a "Details" text label while the
                    Recommendations table used a bare Eye - two icons and two
                    button shapes for the identical action. */}
                <div className="flex shrink-0 gap-1.5">
                  <IconButton
                    onClick={() => navigate(`/anomalies/${anomaly.id}`)}
                    aria-label={`View details for anomaly ${anomaly.id}`}
                    title="View details and take action"
                  >
                    <Eye />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default AnomaliesPage;
